// Utilities Data Entry Form Component
// Monthly data entry for water, gas, and electricity usage

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { utilitiesAPI, flashReportAPI } from '../utils/api';
import '../styles/UtilitiesForm.css';

function UtilitiesForm() {
    const { user } = useAuth();
    
    // State for form inputs
    const [plants, setPlants] = useState([]);
    const [selectedPlant, setSelectedPlant] = useState('');
    const [reportMonth, setReportMonth] = useState('');
    
    // State for form data
    const [formData, setFormData] = useState({
        pounds: '',
        water_gallons: '',
        water_cost_per_gallon: '',
        gas_therms: '',
        gas_cost_per_therm: '',
        electric_kwh: '',
        electric_cost_per_kwh: ''
    });

    // State for previous month's data (for reference display)
    const [previousData, setPreviousData] = useState(null);
    const [previousMonthDate, setPreviousMonthDate] = useState('');

    // State for calculated totals
    const [calculatedTotals, setCalculatedTotals] = useState({
        water_total: 0,
        gas_total: 0,
        electric_total: 0,
        grand_total: 0
    });

    // State for UI feedback
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    // Load plants list when component mounts
    useEffect(() => {
        loadPlants();
        const now = new Date();
        const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        setReportMonth(monthStr);
    }, []);

    // Load previous month's data when report month changes (for reference only)
    useEffect(() => {
        if (selectedPlant && reportMonth) {
            loadPreviousMonthData();
        }
    }, [selectedPlant, reportMonth]);

    // Clear form data when plant or month changes
    useEffect(() => {
        setFormData({
            pounds: '',
            water_gallons: '',
            water_cost_per_gallon: '',
            gas_therms: '',
            gas_cost_per_therm: '',
            electric_kwh: '',
            electric_cost_per_kwh: ''
        });
        setMessage({ text: '', type: '' });
    }, [selectedPlant, reportMonth]);

    // Calculate totals whenever relevant inputs change
    useEffect(() => {
        const waterTotal = (parseFloat(formData.water_gallons) || 0) * (parseFloat(formData.water_cost_per_gallon) || 0);
        const gasTotal = (parseFloat(formData.gas_therms) || 0) * (parseFloat(formData.gas_cost_per_therm) || 0);
        const electricTotal = (parseFloat(formData.electric_kwh) || 0) * (parseFloat(formData.electric_cost_per_kwh) || 0);
        
        setCalculatedTotals({
            water_total: waterTotal,
            gas_total: gasTotal,
            electric_total: electricTotal,
            grand_total: waterTotal + gasTotal + electricTotal
        });
    }, [formData]);

    // Load plants from API
    const loadPlants = async () => {
        try {
            const data = await flashReportAPI.getPlants();
            
            let availablePlants = data.plants;
            
            if (!user.isAdmin) {
                const userPlantIds = user.permissions.map(p => p.plant_id);
                availablePlants = data.plants.filter(p => userPlantIds.includes(p.plant_id));
            }
            
            setPlants(availablePlants);
            
            // Select default plant if user has one, otherwise select first available
            if (availablePlants.length > 0) {
                if (user.default_plant_id) {
                    // Check if user has access to their default plant
                    const hasAccessToDefault = availablePlants.some(p => p.plant_id === user.default_plant_id);
                    if (hasAccessToDefault) {
                        setSelectedPlant(user.default_plant_id.toString());
                    } else {
                        // User doesn't have access to default, use first available
                        setSelectedPlant(availablePlants[0].plant_id.toString());
                    }
                } else {
                    // No default set, use first available
                    setSelectedPlant(availablePlants[0].plant_id.toString());
                }
            }
        } catch (error) {
            setMessage({ text: 'Error loading plants', type: 'error' });
        }
    };

    // Load previous month's data for reference display
    const loadPreviousMonthData = async () => {
        try {
            // Calculate previous month (1 month before selected month)
            const [year, month] = reportMonth.split('-').map(num => parseInt(num));
            let prevYear = year;
            let prevMonth = month - 1;
            
            if (prevMonth < 1) {
                prevMonth = 12;
                prevYear -= 1;
            }
            
            const prevMonthStr = `${prevYear}-${String(prevMonth).padStart(2, '0')}`;
            
            console.log('🔍 Loading previous month data for:', prevMonthStr);
            setPreviousMonthDate(prevMonthStr);
            
            // Try to load previous month's data
            const data = await utilitiesAPI.getReport(selectedPlant, prevMonthStr);
            
            if (data.report) {
                console.log('✅ Previous month data found:', data.report);
                setPreviousData(data.report);
            } else {
                console.log('ℹ️ No previous month data');
                setPreviousData(null);
            }
        } catch (error) {
            console.log('ℹ️ Could not load previous month data');
            setPreviousData(null);
        }
    };

    // Check if user has edit permission for selected plant
    const hasEditPermission = () => {
        if (user.isAdmin) return true;
        
        const permission = user.permissions.find(
            p => p.plant_id === parseInt(selectedPlant)
        );
        
        return permission && permission.permission_level === 'edit';
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Load existing data when "Load Existing" button is clicked
    const handleLoadData = async () => {
        console.log('🔍 UTILITIES - Load Existing clicked!');
        console.log('🔍 UTILITIES - Selected Plant:', selectedPlant);
        console.log('🔍 UTILITIES - Report Month:', reportMonth);
        
        if (!selectedPlant || !reportMonth) {
            console.log('❌ UTILITIES - Missing plant or report month');
            setMessage({ text: 'Please select a plant and report month', type: 'error' });
            return;
        }

        try {
            console.log('🔍 UTILITIES - Making API call to getReport...');
            setLoading(true);
            const data = await utilitiesAPI.getReport(selectedPlant, reportMonth);
            console.log('🔍 UTILITIES - API Response:', data);
            
            if (data.report) {
                console.log('✅ UTILITIES - Report found, loading data...');
                setFormData({
                    pounds: data.report.pounds || '',
                    water_gallons: data.report.water_gallons || '',
                    water_cost_per_gallon: data.report.water_cost_per_gallon || '',
                    gas_therms: data.report.gas_therms || '',
                    gas_cost_per_therm: data.report.gas_cost_per_therm || '',
                    electric_kwh: data.report.electric_kwh || '',
                    electric_cost_per_kwh: data.report.electric_cost_per_kwh || ''
                });
                setMessage({ text: 'Data loaded successfully', type: 'success' });
            } else {
                console.log('ℹ️ UTILITIES - No existing data found');
                setMessage({ text: 'No existing data - ready for new entry', type: 'info' });
            }
        } catch (error) {
            console.error('❌ UTILITIES - Error loading data:', error);
            console.error('❌ UTILITIES - Error response:', error.response?.data);
            setMessage({ text: 'Error loading data', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        console.log('💾 UTILITIES - Save clicked!');
        console.log('💾 UTILITIES - Selected Plant:', selectedPlant);
        console.log('💾 UTILITIES - Report Month:', reportMonth);
        console.log('💾 UTILITIES - Form Data:', formData);

        if (!selectedPlant || !reportMonth) {
            setMessage({ text: 'Please select a plant and report month', type: 'error' });
            return;
        }

        if (!hasEditPermission()) {
            setMessage({ text: 'You do not have edit permission for this plant', type: 'error' });
            return;
        }

        try {
            console.log('💾 UTILITIES - Making API call to createReport...');
            setLoading(true);
            
            // Convert empty strings to 0 for all numeric fields
                const dataToSave = {
                    plant_id: parseInt(selectedPlant),
                    report_month: reportMonth,
                    pounds: formData.pounds === '' ? 0 : parseFloat(formData.pounds),
                    water_gallons: formData.water_gallons === '' ? 0 : parseFloat(formData.water_gallons),
                    water_cost_per_gallon: formData.water_cost_per_gallon === '' ? 0 : parseFloat(formData.water_cost_per_gallon),
                    gas_therms: formData.gas_therms === '' ? 0 : parseFloat(formData.gas_therms),
                    gas_cost_per_therm: formData.gas_cost_per_therm === '' ? 0 : parseFloat(formData.gas_cost_per_therm),
                    electric_kwh: formData.electric_kwh === '' ? 0 : parseFloat(formData.electric_kwh),
                    electric_cost_per_kwh: formData.electric_cost_per_kwh === '' ? 0 : parseFloat(formData.electric_cost_per_kwh)
                };
            
            console.log('💾 UTILITIES - Data to save:', dataToSave);

            const result = await utilitiesAPI.createReport(dataToSave);
            console.log('✅ UTILITIES - Save successful:', result);
            
            setMessage({ text: result.message, type: 'success' });
            
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            console.error('❌ UTILITIES - Error saving data:', error);
            console.error('❌ UTILITIES - Error response:', error.response?.data);
            const errorMsg = error.response?.data?.message || 'Error saving data';
            setMessage({ text: errorMsg, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Clear form
    const handleClear = () => {
        setFormData({
            pounds: '',
            water_gallons: '',
            water_cost_per_gallon: '',
            gas_therms: '',
            gas_cost_per_therm: '',
            electric_kwh: '',
            electric_cost_per_kwh: ''
        });
        setMessage({ text: '', type: '' });
    };

    // Format number for previous data display
    const formatPrevious = (value) => {
        if (!value) return '-';
        return parseFloat(value).toLocaleString('en-US', { maximumFractionDigits: 6 });
    };

    const canEdit = hasEditPermission();
    const selectedPlantName = plants.find(p => p.plant_id.toString() === selectedPlant)?.plant_name || '';

    return (
        <div className="utilities-form-container">
            <h2>Utilities Report - Data Entry</h2>
            <p className="subtitle">Enter monthly utility usage and costs</p>

            {!canEdit && selectedPlant && (
                <div className="permission-warning">
                    <strong>View Only Access:</strong> You have read-only access to {selectedPlantName}. 
                    You cannot enter or modify data.
                </div>
            )}

            {message.text && (
                <div className={`message message-${message.type}`}>
                    {message.text}
                </div>
            )}

            <div className="selection-section">
                <div className="form-group">
                    <label htmlFor="plant">Plant *</label>
                    <select
                        id="plant"
                        value={selectedPlant}
                        onChange={(e) => setSelectedPlant(e.target.value)}
                        disabled={loading}
                    >
                        <option value="">Select a plant...</option>
                        {plants.map(plant => (
                            <option key={plant.plant_id} value={plant.plant_id}>
                                {plant.plant_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="reportMonth">Month (MM/YYYY) *</label>
                    <input
                        type="month"
                        id="reportMonth"
                        value={reportMonth}
                        onChange={(e) => setReportMonth(e.target.value)}
                        disabled={loading}
                    />
                </div>

                <button 
                    type="button" 
                    onClick={handleLoadData}
                    className="btn-secondary"
                    disabled={loading || !selectedPlant || !reportMonth}
                >
                    Load Existing
                </button>
            </div>

            <form onSubmit={handleSubmit} className="utilities-form">
                
                {/* Production Data */}
                <div className="form-section">
                    <h3>Production</h3>
                    <div className="form-row single-column">
                        <div className="form-group">
                            <div className="label-row">
                                <label htmlFor="pounds">Pounds</label>
                                {previousData && previousData.pounds !== null && previousData.pounds !== undefined && (
                                    <span className="previous-badge">
                                        Previous: {formatPrevious(previousData.pounds)}
                                    </span>
                                )}
                            </div>
                            <input
                                type="number"
                                id="pounds"
                                name="pounds"
                                value={formData.pounds}
                                onChange={handleInputChange}
                                step="0.01"
                                placeholder="0.00"
                                disabled={loading || !canEdit}
                                readOnly={!canEdit}
                            />
                        </div>
                    </div>
                </div>

                {/* Water/Sewer Section */}
                <div className="form-section section-water">
                    <h3>Water/Sewer</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <div className="label-row">
                                <label htmlFor="water_gallons">Gallons</label>
                                {previousData && previousData.water_gallons !== null && previousData.water_gallons !== undefined && (
                                    <span className="previous-badge">
                                        Previous: {formatPrevious(previousData.water_gallons)}
                                    </span>
                                )}
                            </div>
                            <input
                                type="number"
                                id="water_gallons"
                                name="water_gallons"
                                value={formData.water_gallons}
                                onChange={handleInputChange}
                                step="0.01"
                                placeholder="0.00"
                                disabled={loading || !canEdit}
                                readOnly={!canEdit}
                            />
                        </div>

                        <div className="form-group">
                            <div className="label-row">
                                <label htmlFor="water_cost_per_gallon">Cost/Gallon ($)</label>
                                {previousData && previousData.water_cost_per_gallon !== null && previousData.water_cost_per_gallon !== undefined && (
                                    <span className="previous-badge">
                                        Previous: {formatPrevious(previousData.water_cost_per_gallon)}
                                    </span>
                                )}
                            </div>
                            <input
                                type="number"
                                id="water_cost_per_gallon"
                                name="water_cost_per_gallon"
                                value={formData.water_cost_per_gallon}
                                onChange={handleInputChange}
                                step="0.000001"
                                placeholder="0.000000"
                                disabled={loading || !canEdit}
                                readOnly={!canEdit}
                            />
                        </div>

                        <div className="form-group">
                            <label>Total Water Cost</label>
                            <input
                                type="text"
                                value={`$${calculatedTotals.water_total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                readOnly
                                className="calculated-field"
                            />
                        </div>
                    </div>
                </div>

                {/* Natural Gas Section */}
                <div className="form-section section-gas">
                    <h3>Natural Gas</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <div className="label-row">
                                <label htmlFor="gas_therms">Therm</label>
                                {previousData && previousData.gas_therms !== null && previousData.gas_therms !== undefined && (
                                    <span className="previous-badge">
                                        Previous: {formatPrevious(previousData.gas_therms)}
                                    </span>
                                )}
                            </div>
                            <input
                                type="number"
                                id="gas_therms"
                                name="gas_therms"
                                value={formData.gas_therms}
                                onChange={handleInputChange}
                                step="0.01"
                                placeholder="0.00"
                                disabled={loading || !canEdit}
                                readOnly={!canEdit}
                            />
                        </div>

                        <div className="form-group">
                            <div className="label-row">
                                <label htmlFor="gas_cost_per_therm">Cost/Therm ($)</label>
                                {previousData && previousData.gas_cost_per_therm !== null && previousData.gas_cost_per_therm !== undefined && (
                                    <span className="previous-badge">
                                        Previous: {formatPrevious(previousData.gas_cost_per_therm)}
                                    </span>
                                )}
                            </div>
                            <input
                                type="number"
                                id="gas_cost_per_therm"
                                name="gas_cost_per_therm"
                                value={formData.gas_cost_per_therm}
                                onChange={handleInputChange}
                                step="0.000001"
                                placeholder="0.000000"
                                disabled={loading || !canEdit}
                                readOnly={!canEdit}
                            />
                        </div>

                        <div className="form-group">
                            <label>Total Gas Cost</label>
                            <input
                                type="text"
                                value={`$${calculatedTotals.gas_total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                readOnly
                                className="calculated-field"
                            />
                        </div>
                    </div>
                </div>

                {/* Electricity Section */}
                <div className="form-section section-electric">
                    <h3>Electricity</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <div className="label-row">
                                <label htmlFor="electric_kwh">KWH</label>
                                {previousData && previousData.electric_kwh !== null && previousData.electric_kwh !== undefined && (
                                    <span className="previous-badge">
                                        Previous: {formatPrevious(previousData.electric_kwh)}
                                    </span>
                                )}
                            </div>
                            <input
                                type="number"
                                id="electric_kwh"
                                name="electric_kwh"
                                value={formData.electric_kwh}
                                onChange={handleInputChange}
                                step="0.01"
                                placeholder="0.00"
                                disabled={loading || !canEdit}
                                readOnly={!canEdit}
                            />
                        </div>

                        <div className="form-group">
                            <div className="label-row">
                                <label htmlFor="electric_cost_per_kwh">Cost/KWH ($)</label>
                                {previousData && previousData.electric_cost_per_kwh !== null && previousData.electric_cost_per_kwh !== undefined && (
                                    <span className="previous-badge">
                                        Previous: {formatPrevious(previousData.electric_cost_per_kwh)}
                                    </span>
                                )}
                            </div>
                            <input
                                type="number"
                                id="electric_cost_per_kwh"
                                name="electric_cost_per_kwh"
                                value={formData.electric_cost_per_kwh}
                                onChange={handleInputChange}
                                step="0.000001"
                                placeholder="0.000000"
                                disabled={loading || !canEdit}
                                readOnly={!canEdit}
                            />
                        </div>

                        <div className="form-group">
                            <label>Total Electric Cost</label>
                            <input
                                type="text"
                                value={`$${calculatedTotals.electric_total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                readOnly
                                className="calculated-field"
                            />
                        </div>
                    </div>
                </div>

                {/* Grand Total */}
                <div className="grand-total-section">
                    <div className="grand-total-label">Total Utilities Cost:</div>
                    <div className="grand-total-value">
                        ${calculatedTotals.grand_total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                </div>

                {canEdit && (
                    <div className="form-actions">
                        <button 
                            type="button"
                            onClick={handleClear}
                            className="btn-clear"
                            disabled={loading}
                        >
                            Clear Form
                        </button>
                        <button 
                            type="submit" 
                            className="btn-primary"
                            disabled={loading || !selectedPlant || !reportMonth}
                        >
                            {loading ? 'Saving...' : 'Save Data'}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
}

export default UtilitiesForm;